import React, { isValidElement, ComponentPropsWithoutRef, ReactElement } from 'react';
import CodeBlock from '@modules/ui/codeBlock/CodeBlock';
import CodeBlockV2 from '@modules/ui/codeBlock/components/CodeBlockV2';
import useCommonCodeBlockStyles from '@modules/miscellaneous/common/styles/codeBlock.styles';
import '@rbx/ui';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import { apiReferenceBasePath } from '@modules/apiReference/page/constants/apiReferenceConstants';
import Mermaid from './Mermaid';
import useMarkdownCodeBlockStyles from './MarkdownCodeBlock.styles';

export type CodeBlockProps = ComponentPropsWithoutRef<'code'>;

export interface MarkdownCodeBlockProps extends CodeBlockProps {
  title?: string;
}

const isValidReactElement = (child: object | null | undefined): child is ReactElement =>
  isValidElement(child);
const handleError = (message: string) => {
  const messageText = `Error in Code Block: ${message}`;
  if (typeof window === 'undefined') {
    throw new Error(messageText);
  } else {
    // eslint-disable-next-line no-console
    console.error(messageText);
  }
};
/**
 * In MDX v2, code blocks generally are all transformed into `code` with fenced
 * code blocks transformed into `code` wrapped by `pre`, which is what this
 * component is for
 */
export default function MarkdownCodeBlock({
  children,
  title,
  ...otherProps
}: MarkdownCodeBlockProps) {
  const {
    classes: { root, minimalCodeBlock },
    cx,
  } = useMarkdownCodeBlockStyles();
  const {
    classes: { codeBlock },
  } = useCommonCodeBlockStyles();
  const { enableEngineReferenceRevamp } = useAppConfig();
  const emptyCodeBlock = <pre />;
  if (Array.isArray(children) && children.length > 1) {
    handleError(`Only 1 child expected for a code block. Got ${children.length}`);
    return emptyCodeBlock;
  }
  let language = 'lua';
  const child = Array.isArray(children) ? children[0] : children;
  if (!isValidReactElement(child)) {
    handleError('Invalid child received for code block');
    return emptyCodeBlock;
  }

  const content =
    typeof child.props.children === 'string' ? child.props.children : child.props.children[0];

  // We get className from `rehype-mdx-code-props`
  // Get the programming language 'python' from className='language-python'
  const match = child.props.className?.match(/^language-(\w+)$/);
  // Example match:
  // [
  //   'language-plain',
  //   'plain',
  //   index: 0,
  //   input: 'language-plain',
  //   groups: undefined
  // ]
  if (match) {
    [, language] = match;
  }

  if (language === 'mermaid') {
    return <Mermaid code={content} />;
  }

  if (typeof content !== 'string') {
    handleError(
      `No code content string found. Got ${JSON.stringify(
        content,
      )} instead. Double check your code block contents.`,
    );
    return emptyCodeBlock;
  }

  // NOTE (tchu, 2025-11-03): Use CodeBlockV2 if engine reference revamp is enabled
  const isEngineReference =
    typeof window !== 'undefined' && window.location.pathname.includes(apiReferenceBasePath);
  if (isEngineReference && enableEngineReferenceRevamp) {
    return (
      <CodeBlockV2
        title={title}
        language={language}
        content={content.trim()}
        enableExpand
        enableCopy
        // NOTE (tchu, 2025-11-05): In Markdown, only show the language label
        // when there's a title to accompany it.
        showLanguageLabel={!!title}
        className={minimalCodeBlock}
      />
    );
  }

  return (
    <pre className={cx(root, codeBlock)}>
      <CodeBlock
        {...otherProps}
        language={language}
        title={title}
        content={content.trim()}
        enableExpand
        enableCopy
        fullWidth
      />
    </pre>
  );
}
